import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { guardiasAPI } from '../../services/api'

const initialState = {
  guardias: [],
  selectedGuardiaId: null,
  bomberos: [],
  plantillas: [],
  estadisticas: null,
  licenciasVerificadas: [],
  intercambios: [],
  loadingIntercambios: false,
  savingIntercambio: false,
  savingReemplazoDirecto: false,
  historial: [],
  loadingHistorial: false,
  configGuardia: null,
  resumen: null,
  loadingResumen: false,
  loading: false,
  savingDay: false,
  exporting: false,
  loadingPlantillas: false,
  savingPlantilla: false,
  aplicandoPlantilla: false,
  aplicandoRolSemanal: false,
  loadingEstadisticas: false,
  error: null,
}

export const fetchGuardiasMensuales = createAsyncThunk(
  'guardias/fetchMensuales',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getMensuales(params)
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible obtener las guardias nocturnas')
    }
  }
)

export const fetchGuardiaMensualById = createAsyncThunk(
  'guardias/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getMensualById(id)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar la guardia nocturna solicitada')
    }
  }
)

export const createGuardiaMensual = createAsyncThunk(
  'guardias/createMensual',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.createMensual(payload)
      return {
        ...response.data?.data,
        dias: [],
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible crear la guardia nocturna')
    }
  }
)

export const updateGuardiaMensual = createAsyncThunk(
  'guardias/updateMensual',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.updateMensual(id, data)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible actualizar la guardia nocturna')
    }
  }
)

export const deleteGuardiaMensual = createAsyncThunk(
  'guardias/deleteMensual',
  async (id, { rejectWithValue }) => {
    try {
      await guardiasAPI.deleteMensual(id)
      return id
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible eliminar la guardia nocturna')
    }
  }
)

export const saveGuardiaDia = createAsyncThunk(
  'guardias/saveDia',
  async ({ guardiaId, data }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.saveDia(guardiaId, data)
      return { guardiaId, dia: response.data?.data }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible guardar la guardia diaria')
    }
  }
)

export const deleteGuardiaDia = createAsyncThunk(
  'guardias/deleteDia',
  async ({ guardiaId, diaId }, { rejectWithValue }) => {
    try {
      await guardiasAPI.deleteDia(diaId)
      return { guardiaId, diaId }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible eliminar la guardia diaria')
    }
  }
)

export const fetchGuardiaBomberos = createAsyncThunk(
  'guardias/fetchBomberos',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getBomberos(params)
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar los bomberos disponibles')
    }
  }
)

export const verificarLicencias = createAsyncThunk(
  'guardias/verificarLicencias',
  async ({ bomberoIds, fechaInicio, fechaFin }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.verificarLicencias({ bomberoIds, fechaInicio, fechaFin })
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible verificar licencias')
    }
  }
)

export const fetchEstadisticasGuardias = createAsyncThunk(
  'guardias/fetchEstadisticas',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getEstadisticas(params)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar estadísticas')
    }
  }
)

export const exportGuardiaMensualPdf = createAsyncThunk(
  'guardias/exportPdf',
  async ({ guardiaId, fileName }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.exportPdf(guardiaId)
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || `guardia_nocturna_${guardiaId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      return true
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible exportar la guardia nocturna a PDF')
    }
  }
)

// ============================================
// THUNKS PARA PLANTILLAS
// ============================================

export const fetchPlantillas = createAsyncThunk(
  'guardias/fetchPlantillas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getPlantillas()
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar las plantillas')
    }
  }
)

export const createPlantilla = createAsyncThunk(
  'guardias/createPlantilla',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.createPlantilla(payload)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible crear la plantilla')
    }
  }
)

export const updatePlantilla = createAsyncThunk(
  'guardias/updatePlantilla',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.updatePlantilla(id, data)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible actualizar la plantilla')
    }
  }
)

export const deletePlantilla = createAsyncThunk(
  'guardias/deletePlantilla',
  async (id, { rejectWithValue }) => {
    try {
      await guardiasAPI.deletePlantilla(id)
      return id
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible eliminar la plantilla')
    }
  }
)

export const aplicarPlantilla = createAsyncThunk(
  'guardias/aplicarPlantilla',
  async ({ guardiaId, plantillaId }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.aplicarPlantilla(guardiaId, plantillaId)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible aplicar la plantilla')
    }
  }
)

export const aplicarRolSemanal = createAsyncThunk(
  'guardias/aplicarRolSemanal',
  async ({ guardiaId, asignaciones }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.aplicarRolSemanal(guardiaId, asignaciones)
      // Devolvemos también diasOmitidos para que la UI pueda avisar (igual que aplicar-plantilla)
      return { guardia: response.data?.data, diasOmitidos: response.data?.diasOmitidos || [] }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible aplicar el rol semanal')
    }
  }
)

// ============================================
// THUNKS PARA INTERCAMBIOS DE GUARDIAS
// ============================================

export const fetchIntercambios = createAsyncThunk(
  'guardias/fetchIntercambios',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getIntercambios(params)
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar los reemplazos')
    }
  }
)

export const crearIntercambio = createAsyncThunk(
  'guardias/crearIntercambio',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.crearIntercambio(payload)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible crear la solicitud de reemplazo')
    }
  }
)

export const responderIntercambio = createAsyncThunk(
  'guardias/responderIntercambio',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.responderIntercambio(id, data)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible responder al reemplazo')
    }
  }
)

export const aprobarIntercambio = createAsyncThunk(
  'guardias/aprobarIntercambio',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.aprobarIntercambio(id, data)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible procesar la aprobación')
    }
  }
)

export const cancelarIntercambio = createAsyncThunk(
  'guardias/cancelarIntercambio',
  async (id, { rejectWithValue }) => {
    try {
      await guardiasAPI.cancelarIntercambio(id)
      return id
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cancelar el reemplazo')
    }
  }
)

// ============================================
// THUNKS GUARDIA v2: reemplazo directo, historial, horario, resumen
// ============================================

export const reemplazoDirecto = createAsyncThunk(
  'guardias/reemplazoDirecto',
  async ({ asignacionId, receptorId, motivo }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.reemplazoDirecto(asignacionId, { receptorId, motivo })
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible aplicar el reemplazo')
    }
  }
)

export const fetchHistorialGuardia = createAsyncThunk(
  'guardias/fetchHistorial',
  async ({ guardiaId, soloCambios } = {}, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getHistorial(guardiaId, soloCambios ? { soloCambios: true } : {})
      return response.data?.data || []
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar el historial')
    }
  }
)

export const fetchConfigGuardia = createAsyncThunk(
  'guardias/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getConfiguracion()
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar el horario de guardia')
    }
  }
)

export const updateConfigGuardia = createAsyncThunk(
  'guardias/updateConfig',
  async (data, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.updateConfiguracion(data)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible guardar el horario de guardia')
    }
  }
)

export const fetchResumenGuardia = createAsyncThunk(
  'guardias/fetchResumen',
  async (guardiaDiaId, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.getResumen(guardiaDiaId)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible cargar el resumen de guardia')
    }
  }
)

export const generarAsistenciaResumen = createAsyncThunk(
  'guardias/generarAsistencia',
  async (guardiaDiaId, { rejectWithValue }) => {
    try {
      await guardiasAPI.generarAsistencia(guardiaDiaId)
      const response = await guardiasAPI.getResumen(guardiaDiaId)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible iniciar el resumen de guardia')
    }
  }
)

export const updateAsistencia = createAsyncThunk(
  'guardias/updateAsistencia',
  async ({ id, presente, observacion }, { rejectWithValue }) => {
    try {
      const payload = {}
      if (presente !== undefined) payload.presente = presente
      if (observacion !== undefined) payload.observacion = observacion
      const response = await guardiasAPI.updateAsistencia(id, payload)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible actualizar la asistencia')
    }
  }
)

export const exportResumenGuardia = createAsyncThunk(
  'guardias/exportResumen',
  async ({ diaId, formato, fileName }, { rejectWithValue }) => {
    try {
      const response = await guardiasAPI.exportResumen(diaId, formato)
      const mime = formato === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const blob = new Blob([response.data], { type: mime })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || `entrega_guardia.${formato === 'pdf' ? 'pdf' : 'xlsx'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      return true
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'No fue posible exportar la entrega de guardia')
    }
  }
)

const guardiasSlice = createSlice({
  name: 'guardias',
  initialState,
  reducers: {
    setSelectedGuardiaId(state, action) {
      state.selectedGuardiaId = action.payload
    },
    clearGuardiasState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuardiasMensuales.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchGuardiasMensuales.fulfilled, (state, action) => {
        state.loading = false
        state.guardias = action.payload.map((guardia) => ({
          ...guardia,
          dias: guardia.dias || [],
        }))

        if (state.guardias.length === 0) {
          state.selectedGuardiaId = null
        } else if (!state.selectedGuardiaId || !state.guardias.some((g) => g.id === state.selectedGuardiaId)) {
          state.selectedGuardiaId = state.guardias[0].id
        }
      })
      .addCase(fetchGuardiasMensuales.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error al obtener las guardias nocturnas'
      })
      .addCase(fetchGuardiaMensualById.fulfilled, (state, action) => {
        if (!action.payload) return
        const index = state.guardias.findIndex((g) => g.id === action.payload.id)
        const payload = {
          ...action.payload,
          dias: action.payload.dias || [],
        }

        if (index >= 0) {
          state.guardias[index] = payload
        } else {
          state.guardias.push(payload)
        }
        state.selectedGuardiaId = payload.id
      })
      .addCase(createGuardiaMensual.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createGuardiaMensual.fulfilled, (state, action) => {
        state.loading = false
        const guardia = {
          ...action.payload,
          dias: action.payload?.dias || [],
        }
        state.guardias.push(guardia)
        state.selectedGuardiaId = guardia.id
      })
      .addCase(createGuardiaMensual.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error al crear la guardia nocturna'
      })
      .addCase(updateGuardiaMensual.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateGuardiaMensual.fulfilled, (state, action) => {
        state.loading = false
        const index = state.guardias.findIndex((g) => g.id === action.payload.id)
        if (index >= 0) {
          state.guardias[index] = {
            ...state.guardias[index],
            ...action.payload,
          }
        }
      })
      .addCase(updateGuardiaMensual.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error al actualizar la guardia nocturna'
      })
      .addCase(deleteGuardiaMensual.fulfilled, (state, action) => {
        state.guardias = state.guardias.filter((g) => g.id !== action.payload)
        if (state.selectedGuardiaId === action.payload) {
          state.selectedGuardiaId = state.guardias[0]?.id || null
        }
      })
      .addCase(saveGuardiaDia.pending, (state) => {
        state.savingDay = true
        state.error = null
      })
      .addCase(saveGuardiaDia.fulfilled, (state, action) => {
        state.savingDay = false
        const guardia = state.guardias.find((g) => g.id === action.payload.guardiaId)
        if (!guardia) return

        const existingIndex = guardia.dias.findIndex((dia) => dia.id === action.payload.dia.id)
        if (existingIndex >= 0) {
          guardia.dias[existingIndex] = action.payload.dia
        } else {
          guardia.dias.push(action.payload.dia)
          guardia.dias.sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
        }
      })
      .addCase(saveGuardiaDia.rejected, (state, action) => {
        state.savingDay = false
        state.error = action.payload || 'Error al guardar la guardia diaria'
      })
      .addCase(deleteGuardiaDia.fulfilled, (state, action) => {
        const guardia = state.guardias.find((g) => g.id === action.payload.guardiaId)
        if (!guardia) return
        guardia.dias = guardia.dias.filter((dia) => dia.id !== action.payload.diaId)
      })
      .addCase(fetchGuardiaBomberos.fulfilled, (state, action) => {
        state.bomberos = action.payload
      })
      .addCase(exportGuardiaMensualPdf.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportGuardiaMensualPdf.fulfilled, (state) => {
        state.exporting = false
      })
      .addCase(exportGuardiaMensualPdf.rejected, (state, action) => {
        state.exporting = false
        state.error = action.payload || 'Error al exportar la guardia nocturna'
      })
      // Plantillas
      .addCase(fetchPlantillas.pending, (state) => {
        state.loadingPlantillas = true
      })
      .addCase(fetchPlantillas.fulfilled, (state, action) => {
        state.loadingPlantillas = false
        state.plantillas = action.payload
      })
      .addCase(fetchPlantillas.rejected, (state, action) => {
        state.loadingPlantillas = false
        state.error = action.payload || 'Error al cargar plantillas'
      })
      .addCase(createPlantilla.pending, (state) => {
        state.savingPlantilla = true
        state.error = null
      })
      .addCase(createPlantilla.fulfilled, (state, action) => {
        state.savingPlantilla = false
        state.plantillas.push(action.payload)
      })
      .addCase(createPlantilla.rejected, (state, action) => {
        state.savingPlantilla = false
        state.error = action.payload || 'Error al crear plantilla'
      })
      .addCase(updatePlantilla.fulfilled, (state, action) => {
        const index = state.plantillas.findIndex((p) => p.id === action.payload.id)
        if (index >= 0) {
          state.plantillas[index] = { ...state.plantillas[index], ...action.payload }
        }
      })
      .addCase(deletePlantilla.fulfilled, (state, action) => {
        state.plantillas = state.plantillas.filter((p) => p.id !== action.payload)
      })
      .addCase(aplicarPlantilla.pending, (state) => {
        state.aplicandoPlantilla = true
        state.error = null
      })
      .addCase(aplicarPlantilla.fulfilled, (state, action) => {
        state.aplicandoPlantilla = false
        if (!action.payload) return
        const index = state.guardias.findIndex((g) => g.id === action.payload.id)
        const payload = {
          ...action.payload,
          dias: action.payload.dias || [],
        }
        if (index >= 0) {
          state.guardias[index] = payload
        }
      })
      .addCase(aplicarPlantilla.rejected, (state, action) => {
        state.aplicandoPlantilla = false
        state.error = action.payload || 'Error al aplicar plantilla'
      })
      .addCase(aplicarRolSemanal.pending, (state) => {
        state.aplicandoRolSemanal = true
        state.error = null
      })
      .addCase(aplicarRolSemanal.fulfilled, (state, action) => {
        state.aplicandoRolSemanal = false
        const guardia = action.payload?.guardia
        if (!guardia) return
        const index = state.guardias.findIndex((g) => g.id === guardia.id)
        const payload = { ...guardia, dias: guardia.dias || [] }
        if (index >= 0) {
          state.guardias[index] = payload
        }
      })
      .addCase(aplicarRolSemanal.rejected, (state, action) => {
        state.aplicandoRolSemanal = false
        state.error = action.payload || 'Error al aplicar rol semanal'
      })
      // Verificar licencias
      .addCase(verificarLicencias.fulfilled, (state, action) => {
        state.licenciasVerificadas = action.payload
      })
      // Estadísticas
      .addCase(fetchEstadisticasGuardias.pending, (state) => {
        state.loadingEstadisticas = true
      })
      .addCase(fetchEstadisticasGuardias.fulfilled, (state, action) => {
        state.loadingEstadisticas = false
        state.estadisticas = action.payload
      })
      .addCase(fetchEstadisticasGuardias.rejected, (state, action) => {
        state.loadingEstadisticas = false
        state.error = action.payload || 'Error al cargar estadísticas'
      })
      // Intercambios
      .addCase(fetchIntercambios.pending, (state) => {
        state.loadingIntercambios = true
        state.error = null
      })
      .addCase(fetchIntercambios.fulfilled, (state, action) => {
        state.loadingIntercambios = false
        state.intercambios = action.payload
      })
      .addCase(fetchIntercambios.rejected, (state, action) => {
        state.loadingIntercambios = false
        state.error = action.payload || 'Error al cargar reemplazos'
      })
      .addCase(crearIntercambio.pending, (state) => {
        state.savingIntercambio = true
      })
      .addCase(crearIntercambio.fulfilled, (state, action) => {
        state.savingIntercambio = false
        if (action.payload) state.intercambios.unshift(action.payload)
      })
      .addCase(crearIntercambio.rejected, (state, action) => {
        state.savingIntercambio = false
        state.error = action.payload || 'Error al crear reemplazo'
      })
      .addCase(responderIntercambio.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.intercambios.findIndex((i) => i.id === action.payload.id)
          if (idx >= 0) state.intercambios[idx] = action.payload
        }
      })
      .addCase(aprobarIntercambio.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.intercambios.findIndex((i) => i.id === action.payload.id)
          if (idx >= 0) state.intercambios[idx] = action.payload
        }
      })
      .addCase(cancelarIntercambio.fulfilled, (state, action) => {
        const idx = state.intercambios.findIndex((i) => i.id === action.payload)
        if (idx >= 0) state.intercambios[idx].estado = 'CANCELADA'
      })
      // Guardia v2: reemplazo directo
      .addCase(reemplazoDirecto.pending, (state) => {
        state.savingReemplazoDirecto = true
        state.error = null
      })
      .addCase(reemplazoDirecto.fulfilled, (state, action) => {
        state.savingReemplazoDirecto = false
        const dato = action.payload
        if (!dato) return
        for (const g of state.guardias) {
          const dia = g.dias?.find((d) => d.id === dato.guardiaDiaId)
          if (dia && Array.isArray(dia.bomberos)) {
            const idx = dia.bomberos.findIndex((b) => b.id === dato.id)
            if (idx >= 0) {
              const previo = dia.bomberos[idx]
              const nombrePrevio = `${previo.bombero?.nombres || ''} ${previo.bombero?.apellidos || ''}`.trim()
              dia.bomberos[idx] = {
                ...previo,
                // El entrante reemplaza al original de la noche (si el saliente ya era reemplazo, a quien él reemplazaba).
                reemplazaA: previo.reemplazaA || (nombrePrevio ? { id: previo.bombero?.id || null, nombre: nombrePrevio } : null),
                comentario: dato.comentario,
                asignadoPor: dato.asignadoPor,
                bombero: dato.bombero,
              }
            }
            break
          }
        }
      })
      .addCase(reemplazoDirecto.rejected, (state, action) => {
        state.savingReemplazoDirecto = false
        state.error = action.payload || 'Error al aplicar el reemplazo'
      })
      // Historial
      .addCase(fetchHistorialGuardia.pending, (state) => {
        state.loadingHistorial = true
      })
      .addCase(fetchHistorialGuardia.fulfilled, (state, action) => {
        state.loadingHistorial = false
        state.historial = action.payload
      })
      .addCase(fetchHistorialGuardia.rejected, (state, action) => {
        state.loadingHistorial = false
        state.error = action.payload || 'Error al cargar el historial'
      })
      // Horario configurable
      .addCase(fetchConfigGuardia.fulfilled, (state, action) => {
        state.configGuardia = action.payload
      })
      .addCase(updateConfigGuardia.fulfilled, (state, action) => {
        state.configGuardia = action.payload
      })
      // Resumen de guardia
      .addCase(fetchResumenGuardia.pending, (state) => {
        state.loadingResumen = true
      })
      .addCase(fetchResumenGuardia.fulfilled, (state, action) => {
        state.loadingResumen = false
        state.resumen = action.payload
      })
      .addCase(fetchResumenGuardia.rejected, (state, action) => {
        state.loadingResumen = false
        state.error = action.payload || 'Error al cargar el resumen de guardia'
      })
      .addCase(generarAsistenciaResumen.fulfilled, (state, action) => {
        state.resumen = action.payload
      })
      .addCase(updateAsistencia.fulfilled, (state, action) => {
        if (!action.payload || !state.resumen?.asistencia) return
        const idx = state.resumen.asistencia.findIndex((a) => a.id === action.payload.id)
        if (idx >= 0) {
          state.resumen.asistencia[idx] = {
            ...state.resumen.asistencia[idx],
            presente: action.payload.presente,
            observacion: action.payload.observacion,
          }
        }
      })
  }
})

export const { setSelectedGuardiaId, clearGuardiasState } = guardiasSlice.actions
export default guardiasSlice.reducer
